import axios from 'axios';
import localforage from 'localforage';
import { clearPrivileges } from 'redux/privileges/actions';
import { logoutUser, setCurrentStaffSubjects } from 'redux/users/actions';
import config from 'utils/config';
import { school_id } from 'utils/constants';

let appStore: any; // Will be injected from your app
export const injectStore = (store: any) => {
  appStore = store;
};

const logOutUser = async () => {
  await localforage.clear();
  if (appStore) {
    appStore.dispatch(clearPrivileges());
    appStore.dispatch(setCurrentStaffSubjects([]));
    appStore.dispatch(logoutUser());
  }

  window.location.href = `${API_BASE_URL?.replace('api', school_id)}/login`;
};

const { API_BASE_URL } = config;

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'X-TENANT-ID': school_id,
  },
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const refreshTokenRequest = async (): Promise<string | null> => {
  const refreshToken = await localforage.getItem<string>('refresh_token');

  if (!refreshToken) {
    await logOutUser();
    return null;
  }

  try {
    const { data } = await axios.post(`${API_BASE_URL}/token`, { refresh_token: refreshToken });
    await localforage.setItem('access_token', data.access_token);
    return data.access_token;
  } catch (error) {
    await logOutUser(); // <=== logout if refresh fails
    return null;
  }
};

// Request Interceptor: Add access token
httpClient.interceptors.request.use(async (config) => {
  const token = await localforage.getItem<string>('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response Interceptor: Handle 401 and retry
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      if (isRefreshing) {
        // Queue this request until token is refreshed
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            httpClient(originalRequest)
              .then((res) => resolve(res.data ?? res))
              .catch(reject);
          });
        });
      }

      originalRequest._retry = true;

      // Start refreshing
      isRefreshing = true;

      const newToken = await refreshTokenRequest();
      isRefreshing = false;

      if (newToken) {
        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return httpClient(originalRequest);
      }
    }

    return Promise.reject(error);
  },
);

export async function httpRequest<T>(request: () => Promise<T>): Promise<T> {
  try {
    const res = await request();
    return res;
  } catch (error: any) {
    const originalRequest = error?.body?.config ?? error?.config;

    if (error?.body?.status === 401) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Queue this request until token is refreshed
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            httpClient(originalRequest)
              .then((res) => resolve(res.data ?? res))
              .catch(reject);
          });
        });
      }

      // Start refreshing
      isRefreshing = true;

      const newToken = await refreshTokenRequest();
      isRefreshing = false; // <== Moved outside the conditional

      if (newToken) {
        onRefreshed(newToken); // Notify queued requests
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        const res = await httpClient(originalRequest);
        return res.data ?? res;
      } else {
        throw error; // Refresh failed
      }
    }

    throw error;
  }
}

// export async function httpRequest<T>(request: () => Promise<T>): Promise<T> {
//   try {
//     const response = await request();
//     return response;
//   } catch (error) {
//     // Optionally, you can log or transform errors here
//     throw error;
//   }
// }

function timeoutPromise<T>(ms: number, promise: Promise<T>): Promise<T> {
  // Use a much shorter timeout if browser is offline
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const timeoutDuration = isOffline ? 3000 : ms; // 3 seconds timeout when offline

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const errorMessage = isOffline
        ? 'You are currently offline. Please check your internet connection and try again.'
        : 'We experienced a timeout, please try again.';

      const error: any = new Error(errorMessage);
      error.name = 'TimeoutError';
      error.response = {
        data: {
          message: errorMessage,
        },
        status: isOffline ? 600 : 409, // Use 600 status code for offline errors
      };
      reject(error);
    }, timeoutDuration);

    promise.then(
      (res) => {
        clearTimeout(timeoutId);
        resolve(res);
      },
      (err) => {
        clearTimeout(timeoutId);
        // Add network error detection
        if (err.message === 'Network Error' || (err.response && err.response.status === 0)) {
          const networkError: any = new Error(
            'You are currently offline. Please check your internet connection and try again.',
          );
          networkError.name = 'NetworkError';
          networkError.response = {
            data: {
              message:
                'You are currently offline. Please check your internet connection and try again.',
            },
            status: 600,
          };
          reject(networkError);
        } else {
          reject(err);
        }
      },
    );
  });
}

export async function apiWrapper<T>(request: () => Promise<T>) {
  try {
    // Check if offline before making request
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const offlineError: any = new Error(
        'You are currently offline. Please check your internet connection and try again.',
      );
      offlineError.name = 'OfflineError';
      offlineError.response = {
        data: {
          message:
            'You are currently offline. Please check your internet connection and try again.',
        },
        status: 600,
      };
      throw offlineError;
    }

    const response = await timeoutPromise(1200000, httpRequest(request));
    return response;
  } catch (error) {
    throw error;
  }
}

export const handleAccessToken = async () => {
  const token = await localforage.getItem('access_token');
  httpClient.defaults.headers.common.Authorization = token ? `Bearer ${token}` : null;
};

export default httpClient;
